import {DocumentNode, gql} from '@apollo/client';
import {entry, objectFromEntries} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {sortNodesByCoordinates} from 'procedures/design/flowchartManagementUtils';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import {fillPlanMethodVersionWithPlannedValuesAndConfirmationMethods} from 'procedures/utils/fillPlanMethodVersionWithPlannedValuesAndConfirmationMethods';
import {getPowAgnosticTemplateFlowchartVersionFromProcedureRun} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {
  EXPERIMENTAL_INPUTS_FLOWCHART_FRAGMENT,
  ExperimentFlowchart,
  ExperimentMethod,
  getValuesForMethod,
  prepareInitialExperimentalInputs,
  ProcedureMethodDefinitionVersionNodeParameters,
} from 'studies/typed/shared/ExperimentalInputs';
import type {
  ConditionReplicateEdge,
  MethodExecutionInstanceInfoForPlacement,
} from 'studies/typed/shared/ProcedureRunDesignFlowchartLayoutUtils';
import {ConditionGroup} from 'studies/typed/shared/useExperimentDesignState';

import {GetStudyDesignFromProcedureRunGraphQL as ProcedureRunNode} from './__generated/GetStudyDesignFromProcedureRunGraphQL';

interface StudyDesignParams {
  experimentalInputs: ExperimentalInputs;
  templateFlowchart: ExperimentFlowchart;
  /** Condition groups loaded from the procedure run; by definition all persisted. */
  initialPersistedConditionGroups: Array<ConditionGroup>;
}

export interface MethodExecutionStatus {
  methodId: string;
  conditionId: string;
  replicateNumber: number;
  status: ProcessTaskStatusType | null;
}

/**
 * Extracts each accessible PMEI's info for placement (method + condition-replicate) from a procedure run.
 * PMEIs with an inaccessible template method are dropped, mirroring {@link getMethodExecutionStatuses}.
 */
export function getMethodExecutionInstanceInfoForPlacements(
  procedureRun: ProcedureRunNode
): Array<MethodExecutionInstanceInfoForPlacement> {
  return procedureRun.methodExecutionInstances
    .filter((pmei) => !isInaccessible(pmei.templateMethodVersion))
    .map((pmei) => {
      const replicate = pmei.procedureRunConditionReplicate;
      return {
        pmeiId: pmei.id,
        methodId: getAccessibleOrInaccessibleId(pmei.templateMethodVersion),
        conditionId: replicate.procedureRunCondition.id,
        replicateNumber: replicate.replicateNumber,
      };
    });
}

/**
 * Extracts the runtime condition graph's directed edges as source/target PMEI id pairs.
 * Resolving endpoints to grid nodes (and dropping any that don't resolve) happens downstream.
 */
export function getConditionReplicateEdges(procedureRun: ProcedureRunNode): Array<ConditionReplicateEdge> {
  return procedureRun.conditionReplicateFlowchart.map((edge) => {
    const sourcePmeiId = edge.sourceProcedureMethodExecutionInstance.id;
    const targetPmeiId = edge.targetProcedureMethodExecutionInstance.id;
    return {sourcePmeiId, targetPmeiId};
  });
}

/**
 * Produces one flattened status record per method execution instance (PMEI). `status` is the raw
 * task status, or `null` when the method task is missing or inaccessible.
 *
 * PMEIs with an inaccessible template method are dropped
 * so their cells render unstyled rather than crashing the design tab.
 */
export function getMethodExecutionStatuses(procedureRun: ProcedureRunNode): Array<MethodExecutionStatus> {
  return procedureRun.methodExecutionInstances
    .filter((pmei) => !isInaccessible(pmei.templateMethodVersion))
    .map((pmei) => {
      const replicate = pmei.procedureRunConditionReplicate;
      return {
        methodId: getAccessibleOrInaccessibleId(pmei.templateMethodVersion),
        conditionId: replicate.procedureRunCondition.id,
        replicateNumber: replicate.replicateNumber,
        status:
          pmei.methodTask == null || isInaccessible(pmei.methodTask)
            ? null
            : pmei.methodTask.status.statusType,
      };
    });
}

/**
 * Extracts study design state from a `ProcedureRunNode`: condition groups (built from
 * `procedureRun.conditions`), the template flowchart, and the per-group × per-method
 * `ExperimentalInputs` cell values reconstituted from each PMEI's planned values.
 *
 * `requireBowlingLaneCoverage`:
 *  - `true` (split-pool off) — every condition group must cover every flowchart method.
 *    Throws otherwise. Without split-pool, users only create full bowling-lane conditions.
 *  - `false` (split-pool on) — sparse per-method groups are accepted. A method with no PMEI
 *    for a group falls back to the template method's default values for that cell, rendered
 *    as "no override".
 */
export default function getStudyDesignFromProcedureRun(
  procedureRun: ProcedureRunNode,
  {requireBowlingLaneCoverage}: {requireBowlingLaneCoverage: boolean}
): StudyDesignParams {
  // This is the flowchart version that corresponds to the procedure the user selected to initiate the experiment.
  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRun(procedureRun);
  assert(!isInaccessible(flowchartVersion), 'Expected flowchart version to be accessible');
  const templateMethods: Array<ExperimentMethod> = sortNodesByCoordinates(
    [...flowchartVersion.nodes],
    'gridLayoutCoordinates'
  );
  const templateFlowchart = {...flowchartVersion, nodes: templateMethods};

  const templateMethodVersionIds = templateMethods.map((m) => m.id);
  const initialPersistedConditionGroups: Array<ConditionGroup> = buildConditionGroupsFromBackendConditions(
    procedureRun.conditions,
    templateMethodVersionIds
  );
  if (requireBowlingLaneCoverage) {
    for (const group of initialPersistedConditionGroups) {
      const missing = templateMethodVersionIds.filter((id) => group.conditionIdsByPmdvId[id] == null);
      assert(
        missing.length === 0,
        `Condition group "${group.name}" is missing entries for method versions [${missing.join(', ')}]`
      );
    }
  }

  // Invariant: a backend `ProcedureRunCondition.id` belongs to exactly one condition group.
  const conditionGroupIndexByConditionId = new Map<string, number>();
  initialPersistedConditionGroups.forEach((group, groupIndex) => {
    for (const conditionId of Object.values(group.conditionIdsByPmdvId)) {
      conditionGroupIndexByConditionId.set(conditionId, groupIndex);
    }
  });

  // Bucket each PMEI's planned values by (templateMethodVersionId, conditionGroupIndex) so
  // the reconstituted ExperimentalInputs values fall into the right group × method.
  const planMethodByTemplateMethodAndConditionGroup: {
    [methodId: string]: {[conditionGroupIndex: number]: ProcedureMethodDefinitionVersionNodeParameters};
  } = {};
  procedureRun.methodExecutionInstances.forEach((pmei) => {
    const templateMethodVersionId = getAccessibleOrInaccessibleId(pmei.templateMethodVersion);
    const conditionGroupIndex = conditionGroupIndexByConditionId.get(
      pmei.procedureRunConditionReplicate.procedureRunCondition.id
    );
    assert(
      conditionGroupIndex != null,
      `PMEI references condition "${pmei.procedureRunConditionReplicate.procedureRunCondition.id}" not in any group`
    );
    const parameterConfirmationMethodsByParameterId = new Map(
      pmei.parameterConfirmationMethods.map(({parameterId, confirmationMethod}) => [
        parameterId,
        confirmationMethod,
      ])
    );
    planMethodByTemplateMethodAndConditionGroup[templateMethodVersionId] ??= {};
    planMethodByTemplateMethodAndConditionGroup[templateMethodVersionId][conditionGroupIndex] =
      fillPlanMethodVersionWithPlannedValuesAndConfirmationMethods(
        pmei.planMethodDefinitionVersion,
        pmei.dataParameterPlannedValues,
        pmei.materialInputPlannedValues,
        pmei.equipmentPlannedValues,
        parameterConfirmationMethodsByParameterId,
        // For process intermediates, we do want to include the fallback template info
        {fallbackToPlanMaterialInputs: true}
      );
  });
  if (requireBowlingLaneCoverage) {
    // In legacy mode every (template method × condition group) cell must have a PMEI; the
    // downstream `?? methodNode` and per-setting template fallbacks would otherwise hide
    // missing-PMEI backend bugs as silent "no override" cells. Crash early instead.
    for (const methodNode of templateMethods) {
      const buckets = planMethodByTemplateMethodAndConditionGroup[methodNode.id];
      assert(buckets != null, `Missing PMEIs for method "${methodNode.id}"`);
      for (
        let conditionGroupIndex = 0;
        conditionGroupIndex < initialPersistedConditionGroups.length;
        conditionGroupIndex++
      ) {
        assert(
          buckets[conditionGroupIndex] != null,
          `Missing PMEI for (method="${methodNode.id}", conditionGroupIndex=${conditionGroupIndex})`
        );
      }
    }
  }
  // Reconstitute per-method cell values. The `?? methodNode` fallback fires for sparse
  // (method, conditionGroup) cells when split-pool is on, rendered as "no override". When
  // split-pool is off the assert above guarantees the fallback is unreachable.
  const planValues = objectFromEntries(
    templateMethods.map((methodNode) =>
      entry(methodNode.id, {
        values: initialPersistedConditionGroups.map((_, conditionGroupIndex) =>
          getValuesForMethod(
            planMethodByTemplateMethodAndConditionGroup[methodNode.id]?.[conditionGroupIndex] ?? methodNode
          )
        ),
      })
    )
  );
  const prevConfirmationMethods = objectFromEntries(
    templateMethods.map((methodNode) =>
      entry(methodNode.id, {
        parameterConfirmationMethods: getPlannedParameterConfirmationMethods(
          methodNode,
          planMethodByTemplateMethodAndConditionGroup[methodNode.id]
        ),
      })
    )
  );
  const experimentalInputs = prepareInitialExperimentalInputs(
    initialPersistedConditionGroups.length,
    templateMethods,
    {prevExperimentalInputValues: planValues, prevConfirmationMethods}
  );

  return {experimentalInputs, templateFlowchart, initialPersistedConditionGroups};
}

/**
 * Returns the parameter confirmation methods to seed the design state with for `templateMethod`,
 * one entry per data parameter / material input / equipment setting.
 *
 * Each PMEI carries the plan-level `confirmationMethod` for one `(templateMethod,
 * conditionGroup)` cell. `planMethodsByConditionGroupIndex` is the bucketed-by-group set of
 * PMEIs for this template method; we pick the first one as the reference (the per-replicate
 * asserts below guarantee they all agree on confirmation methods).
 *
 * Per-setting fallback to the template method's own `confirmationMethod` fires when no PMEI
 * is present for that setting — only possible when split-pool is on
 * (`requireBowlingLaneCoverage: false`). Under the bowling-lane invariant every cell has a
 * PMEI, so the fallback is unreachable.
 */
function getPlannedParameterConfirmationMethods(
  templateMethod: ExperimentMethod,
  planMethodsByConditionGroupIndex:
    | {[conditionGroupIndex: number]: ProcedureMethodDefinitionVersionNodeParameters}
    | undefined
): ExperimentalInputs[string]['parameterConfirmationMethods'] {
  const planMethods = Object.values(planMethodsByConditionGroupIndex ?? {});
  const referencePlanMethod = planMethods[0];

  // Per-template-method backend invariants: every planMethod for this template method must
  // have the same parameter shape as the template, and must agree with the reference
  // planMethod on every per-parameter confirmation method. Validates both modes whenever we
  // have at least one planMethod to inspect; in sparse mode `planMethods` may be empty, in
  // which case there's nothing to validate and we fall through to template defaults.
  planMethods.forEach((planMethod) => {
    assert(
      planMethod.dataParameterSettings.length === templateMethod.dataParameterSettings.length,
      'Expected planned method to have same number of data parameters as template method'
    );
    planMethod.dataParameterSettings.forEach(({version: {name, confirmationMethod}}, i) => {
      assert(
        name === templateMethod.dataParameterSettings[i].version.name,
        'Expected planned method to have same order of data parameters as template method'
      );
      assert(
        confirmationMethod === referencePlanMethod.dataParameterSettings[i].version.confirmationMethod,
        'Expected all planned methods to have same parameter confirmation methods'
      );
    });
    assert(
      planMethod.materialInputSettings.length === templateMethod.materialInputSettings.length,
      'Expected planned method to have same number of material inputs as template method'
    );
    planMethod.materialInputSettings.forEach(({version: {name, confirmationMethod}}, i) => {
      assert(
        name === templateMethod.materialInputSettings[i].version.name,
        'Expected planned method to have same order of material inputs as template method'
      );
      assert(
        confirmationMethod === referencePlanMethod.materialInputSettings[i].version.confirmationMethod,
        'Expected all planned methods to have same material confirmation methods'
      );
    });
    assert(
      planMethod.equipmentSettings.length === templateMethod.equipmentSettings.length,
      'Expected planned method to have same number of equipment as template method'
    );
    planMethod.equipmentSettings.forEach(({version: {name, confirmationMethod}}, i) => {
      assert(
        name === templateMethod.equipmentSettings[i].version.name,
        'Expected planned method to have same order of equipment as template method'
      );
      assert(
        confirmationMethod === referencePlanMethod.equipmentSettings[i].version.confirmationMethod,
        'Expected all planned methods to have same equipment confirmation methods'
      );
    });
  });

  return {
    parameters: templateMethod.dataParameterSettings.map(
      (templateSetting, i) =>
        referencePlanMethod?.dataParameterSettings[i].version.confirmationMethod ??
        templateSetting.version.confirmationMethod
    ),
    materialInputs: templateMethod.materialInputSettings.map(
      (templateSetting, i) =>
        referencePlanMethod?.materialInputSettings[i].version.confirmationMethod ??
        templateSetting.version.confirmationMethod
    ),
    equipment: templateMethod.equipmentSettings.map(
      (templateSetting, i) =>
        referencePlanMethod?.equipmentSettings[i].version.confirmationMethod ??
        templateSetting.version.confirmationMethod
    ),
  };
}

/**
 * Converts a `ProcedureRun`'s `conditions` list into {@link ConditionGroup}s, handling a mix
 * of shared (`templateMethodVersion: null`) and per-method conditions.
 *
 * Grouping strategy: walk the backend conditions in order. A shared condition becomes a group
 * on its own, with its id replicated across every entry of `conditionIdsByPmdvId` (one per
 * flowchart method). A per-method condition is folded into an existing group keyed by
 * `(name, description, numReplicates)` if one exists, populating only its owning method's
 * entry; otherwise it starts a new group. Groups preserve first-encounter order.
 *
 * Throws on a duplicate per-method entry for the same method within a single group — a
 * backend data integrity violation.
 */
export function buildConditionGroupsFromBackendConditions(
  backendConditions: ReadonlyArray<ProcedureRunNode['conditions'][number]>,
  templateMethodVersionIds: ReadonlyArray<string>
): Array<ConditionGroup> {
  const groups: Array<ConditionGroup> = [];
  const perMethodGroupIndexByKey = new Map<string, number>();

  for (const condition of backendConditions) {
    const numReplicates = condition.procedureRunConditionReplicates.length;

    if (condition.templateMethodVersion == null) {
      // TODO(BNCH-154831): drop this branch after the per-method backfill — `templateMethodVersion`
      // is never null in the new world.
      const conditionIdsByPmdvId: Record<string, string> = {};
      for (const methodId of templateMethodVersionIds) {
        conditionIdsByPmdvId[methodId] = condition.id;
      }
      groups.push({
        name: condition.name,
        description: condition.description,
        numReplicates,
        conditionIdsByPmdvId,
      });
      continue;
    }

    const methodVersionId = getAccessibleOrInaccessibleId(condition.templateMethodVersion);
    const groupKey = JSON.stringify([condition.name, condition.description, numReplicates]);
    const existingIndex = perMethodGroupIndexByKey.get(groupKey);
    if (existingIndex == null) {
      perMethodGroupIndexByKey.set(groupKey, groups.length);
      groups.push({
        name: condition.name,
        description: condition.description,
        numReplicates,
        conditionIdsByPmdvId: {[methodVersionId]: condition.id},
      });
    } else {
      const group = groups[existingIndex];
      assert(
        group.conditionIdsByPmdvId[methodVersionId] == null,
        `Duplicate per-method condition for method "${methodVersionId}" in condition group "${condition.name}"`
      );
      group.conditionIdsByPmdvId[methodVersionId] = condition.id;
    }
  }

  return groups;
}

export const STUDY_DESIGN_FROM_PROCEDURE_RUN_FRAGMENT: DocumentNode = gql`
  fragment GetStudyDesignFromProcedureRunGraphQL on ProcedureRunNode {
    id
    name
    templateProcedureFlowchartVersion {
      ... on ProcedureFlowchartVersionNode {
        ...ExperimentalInputsFlowchartGraphQL
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
    procedureFlowchartConfigVersionProxy {
      ...ProcedureFlowchartConfigVersionProxyAllFieldsGraphQL
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
    methodExecutionInstances {
      ...ProcedureMethodExecutionInstancePlannedValuesGraphQL
      planMethodDefinitionVersion {
        ...BaseProcedureMethodDefinitionVersionGraphQL
        materialInputSettings {
          templateValues {
            id
            material
            amount
          }
        }
      }
      parameterConfirmationMethods {
        parameterId
        confirmationMethod
      }
      templateMethodVersion {
        ... on ProcedureMethodDefinitionVersionNode {
          id
        }
        ... on InaccessibleInfoNode {
          ...InaccessibleInfoGraphQL
        }
      }
      methodTask {
        ... on ProcessTaskNode {
          id
          status {
            ... on ProcessTaskStatusNode {
              id
              statusType
            }
          }
        }
        ... on InaccessibleInfoNode {
          ...InaccessibleInfoGraphQL
        }
      }
      procedureRunConditionReplicate {
        id
        replicateNumber
        procedureRunCondition {
          id
        }
      }
    }
    conditions {
      id
      name
      description
      templateMethodVersion {
        ... on ProcedureMethodDefinitionVersionNode {
          id
        }
        ... on InaccessibleInfoNode {
          ...InaccessibleInfoGraphQL
        }
      }
      procedureRunConditionReplicates {
        id
      }
    }
    conditionReplicateFlowchart {
      sourceProcedureMethodExecutionInstance {
        id
      }
      targetProcedureMethodExecutionInstance {
        id
      }
    }
  }
  ${getFragmentWrapper('ProcedureMethodExecutionInstancePlannedValues').fragment}
  ${getFragmentWrapper('BaseProcedureMethodDefinitionVersion').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ProcedureFlowchartConfigVersionProxyAllFields').fragment}
  ${EXPERIMENTAL_INPUTS_FLOWCHART_FRAGMENT}
`;
